import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getLastUserId, useAuth } from './AuthContext';

/**
 * Route guard. Mirrors the Razor conventions: everything is authorised by default,
 * with named pages opting into a role via the `roles` prop.
 */
export function RequireAuth({ roles }: { roles?: string[] }) {
  const { isAuthenticated, initialising, user } = useAuth();
  const location = useLocation();

  if (initialising) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve where the user was heading so login can bounce them back - but only to the same
    // account. fromUserId records who was signed in last; LoginPage ignores `from` for anyone else.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search, fromUserId: getLastUserId() }}
      />
    );
  }

  // An account on a password somebody else chose - emailed at sign-up or after an administrator
  // reset - can reach nothing but the change-password screen. Without this the emailed password
  // quietly becomes permanent for anyone who never replaces it, which is most of what makes
  // emailing one acceptable at all. Mirrors the middleware in web/Program.cs.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // The platform requires two-step sign-in (Platform Settings → Security) and this account has not
  // set it up: My Profile, where the set-up card is, is the only page. The password comes first.
  if (user?.twoFactorSetupRequired && location.pathname !== '/account/profile') {
    return <Navigate to="/account/profile" replace />;
  }

  if (roles && roles.length > 0 && (!user || !roles.includes(user.role))) {
    return <Navigate to="/access-denied" replace />;
  }

  return <Outlet />;
}
